/**
 * docs/UX.md 18 · `/m/users` 账号（仅 admin）
 *
 * ACCOUNTS.md §1/§2/§4: an admin never sets or sees a plaintext password. The
 * only two ways in are a one-time invitation link (roles / class / 专家方向
 * preset) and a one-time reset link. Every action here lands in `audit_log`.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Role } from "@xinghai/shared";
import {
  Button,
  EmptyState,
  Field,
  Icon,
  Input,
  Select,
  Sheet,
  Table,
  useConfirmSafe,
  useToast,
} from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { auditApi, classesApi, invitationsApi, usersApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { ViewAsStudentButton } from "./ViewAsStudentButton";
import {
  CopyField,
  ErrorNotice,
  KeyValue,
  LoadingBlock,
  PageHeader,
  QueryBoundary,
  Section,
  StatusTag,
  formatDateTime,
} from "./common";
import {
  ANNOTATOR_TRACKS,
  ANNOTATOR_TRACK_LABEL,
  INVITE_EXPIRY_OPTIONS,
  INVITE_TARGETS,
  INVITE_TRACKS,
  ROLE_LABEL,
  ROLE_ORDER,
  auditActionLabel,
} from "./labels";
import type { AdminUser, AnnotatorTrack, AuditEntry, Invitation } from "./types";

/** Short typable code `XH-XXXX-XXXX`; falls back to the raw token. */
export function invitationCode(inv: Pick<Invitation, "code" | "token">): string {
  return inv.code ?? inv.token;
}

/**
 * pending / used / expired / revoked — so an icon always carries the state.
 *
 * The server sends its own `status` (`open` / `used` / `expired` / `revoked`).
 * Trust it, and only re-derive for an older payload that has no `status`.
 * Revoking sets `revoked_at` and keeps the row.
 */
export type InviteState = "pending" | "used" | "expired" | "revoked";

export function invitationState(inv: Invitation, now: number = Date.now()): InviteState {
  if (inv.revoked_at) return "revoked";
  if (inv.status) return inv.status === "open" ? "pending" : inv.status;
  if (inv.used_at) return "used";
  const expires = Date.parse(inv.expires_at);
  if (Number.isFinite(expires) && expires <= now) return "expired";
  return "pending";
}

const INVITE_STATE_META: Record<
  InviteState,
  {
    label: string;
    icon: "ui-clock" | "ui-check-circle" | "ui-history" | "ui-ban";
    tone: "info" | "success" | "neutral" | "danger";
  }
> = {
  pending: { label: "待使用", icon: "ui-clock", tone: "info" },
  used: { label: "已使用", icon: "ui-check-circle", tone: "success" },
  expired: { label: "已过期", icon: "ui-history", tone: "neutral" },
  revoked: { label: "已撤销", icon: "ui-ban", tone: "danger" },
};

export function UsersPage() {
  const { me, isLoading } = useAuth();
  const isAdmin = (me?.user.roles ?? []).includes("admin");

  if (isLoading) {
    return (
      <div className="xz-adm">
        <PageHeader title="账号" />
        <LoadingBlock rows={2} label="正在检查权限" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="xz-adm">
        <PageHeader title="账号" />
        <EmptyState
          icon={<Icon id="ui-lock" size={24} />}
          title="无权限"
          description="账号管理只对系统管理员开放。需要新建或停用账号时，请联系管理员。"
        />
      </div>
    );
  }

  return <UsersAdmin selfId={me?.user.id ?? null} />;
}

function UsersAdmin({ selfId }: { selfId: string | null }) {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [auditFor, setAuditFor] = useState<AdminUser | null>(null);
  const [resetLink, setResetLink] = useState<{
    user: AdminUser;
    url: string;
    expires_at: string;
  } | null>(null);

  const list = useQuery({
    queryKey: qk.adminUsers,
    queryFn: ({ signal }) => usersApi.list(signal),
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = list.data?.users ?? [];
    if (!q) return all;
    return all.filter(
      (u) => u.username.toLowerCase().includes(q) || u.display_name.toLowerCase().includes(q),
    );
  }, [list.data, query]);

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof usersApi.update>[1] }) =>
      usersApi.update(id, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminUsers }),
    onError: (e: Error) => toast.error(e.message),
  });

  const makeResetLink = useMutation({
    mutationFn: (u: AdminUser) => usersApi.resetLink(u.id),
    onSuccess: (r, u) => setResetLink({ user: u, url: r.url, expires_at: r.expires_at }),
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleDisabled = async (u: AdminUser) => {
    if (u.disabled) {
      await update.mutateAsync({ id: u.id, patch: { disabled: false } });
      toast.success(`已启用 ${u.username}`);
      return;
    }
    const ok = await confirm({
      title: `停用账号 ${u.username}？`,
      message: "停用会立即吊销该账号的全部会话，对方将无法登录。账号不会被删除，审计记录保留。",
      confirmLabel: "停用",
      danger: true,
    });
    if (!ok) return;
    await update.mutateAsync({ id: u.id, patch: { disabled: true } });
    toast.success(`已停用 ${u.username}`);
  };

  const askResetLink = async (u: AdminUser) => {
    const ok = await confirm({
      title: `为 ${u.username} 生成重置链接？`,
      message:
        "会生成一条 30 分钟有效的一次性链接，并吊销该账号现有会话。链接只显示一次，请立刻转交本人。",
      confirmLabel: "生成链接",
      danger: true,
    });
    if (ok) makeResetLink.mutate(u);
  };

  const columns: Array<Column<AdminUser>> = [
    {
      key: "username",
      header: "用户名",
      render: (u) => <span className="xz-adm__mono">{u.username}</span>,
    },
    { key: "name", header: "姓名", render: (u) => u.display_name },
    {
      key: "roles",
      header: "角色",
      render: (u) => (
        <span className="xz-adm__tags">
          {u.roles.map((r) => (
            <StatusTag key={r} icon="ui-user">
              {ROLE_LABEL[r]}
            </StatusTag>
          ))}
        </span>
      ),
    },
    {
      key: "track",
      header: "专家方向",
      render: (u) =>
        u.track ? (
          <StatusTag icon="ui-target" tone="info">
            {ANNOTATOR_TRACK_LABEL[u.track] ?? u.track}
          </StatusTag>
        ) : (
          <span className="gz-muted">—</span>
        ),
    },
    {
      key: "overlap",
      header: "师生回避",
      hideOnMobile: true,
      render: (u) =>
        u.teacher_overlap_count == null ? (
          <span className="gz-muted">—</span>
        ) : u.teacher_overlap_count > 0 ? (
          <StatusTag icon="ui-shield" tone="warning">
            回避 {u.teacher_overlap_count} 条
          </StatusTag>
        ) : (
          <StatusTag icon="ui-check">无重叠</StatusTag>
        ),
    },
    {
      key: "login",
      header: "最近登录",
      hideOnMobile: true,
      render: (u) =>
        u.last_login_at === undefined ? (
          <span className="gz-muted">—</span>
        ) : (
          <span>
            {formatDateTime(u.last_login_at)}
            {u.session_count != null && (
              <span className="gz-caption"> · {u.session_count} 个会话</span>
            )}
          </span>
        ),
    },
    {
      key: "state",
      header: "状态",
      render: (u) =>
        u.disabled ? (
          <StatusTag icon="ui-ban" tone="danger">
            已停用
          </StatusTag>
        ) : (
          <StatusTag icon="ui-check-circle" tone="success">
            正常
          </StatusTag>
        ),
    },
    {
      key: "actions",
      header: "操作",
      render: (u) => (
        <span className="xz-adm__rowactions">
          {u.roles.includes("student") && (
            <ViewAsStudentButton userId={u.id} disabled={u.disabled} />
          )}
          <Button size="sm" variant="secondary" onClick={() => setEditing(u)}>
            编辑角色/方向
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void askResetLink(u)}>
            生成重置链接
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAuditFor(u)}>
            查看审计
          </Button>
          <Button
            size="sm"
            variant={u.disabled ? "secondary" : "danger"}
            disabled={u.id === selfId}
            onClick={() => void toggleDisabled(u)}
          >
            {u.disabled ? "启用" : "停用"}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="xz-adm">
      <PageHeader
        title="账号"
        description="管理员不设定也看不到任何密码：新人用一次性邀请链接自己设密码，忘记密码用一次性重置链接。"
        actions={
          <Button leadingIcon={<Icon id="ui-key" size={16} />} onClick={() => setInviteOpen(true)}>
            生成邀请链接
          </Button>
        }
      />

      <div className="xz-adm__toolbar">
        <Field label="搜索">
          <Input
            type="search"
            value={query}
            placeholder="用户名或姓名"
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
      </div>

      <QueryBoundary
        isPending={list.isPending}
        isError={list.isError}
        error={list.error}
        onRetry={() => void list.refetch()}
      >
        <Table
          caption="账号列表"
          columns={columns}
          rows={rows}
          rowKey={(u) => u.id}
          empty={<EmptyState title="没有符合条件的账号" description="换个搜索词试试。" />}
        />
      </QueryBoundary>

      <InvitationsSection />

      <InviteSheet open={inviteOpen} onClose={() => setInviteOpen(false)} />
      {editing && <EditUserSheet user={editing} onClose={() => setEditing(null)} />}
      {auditFor && <AuditSheet user={auditFor} onClose={() => setAuditFor(null)} />}

      {resetLink && (
        <Sheet
          open
          onClose={() => setResetLink(null)}
          title={`${resetLink.user.username} 的重置链接`}
          footer={
            <Button variant="secondary" onClick={() => setResetLink(null)}>
              我已转交
            </Button>
          }
        >
          <div className="xz-form">
            <div className="gz-notice gz-notice--success" role="status">
              <Icon id="ui-key" size={20} />
              <span>
                这条链接只显示这一次，关闭后无法再查看。有效期至{" "}
                {formatDateTime(resetLink.expires_at)}。
              </span>
            </div>
            <CopyField
              label="一次性重置链接"
              value={resetLink.url}
              hint="发给本人，对方打开后自行设置新密码；链接用过即失效。"
            />
          </div>
        </Sheet>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// 邀请记录
// --------------------------------------------------------------------------- //

function InvitationsSection() {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.adminInvitations,
    queryFn: ({ signal }) => invitationsApi.list(signal),
  });

  const revoke = useMutation({
    mutationFn: (code: string) => invitationsApi.revoke(code),
    onSuccess: () => {
      toast.success("邀请链接已撤销");
      void qc.invalidateQueries({ queryKey: qk.adminInvitations });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const askRevoke = async (inv: Invitation) => {
    const ok = await confirm({
      title: "撤销这条邀请链接？",
      message: "撤销之后这条链接立刻失效，已经发出去的链接也打不开了。",
      confirmLabel: "撤销",
      danger: true,
    });
    if (ok) revoke.mutate(invitationCode(inv));
  };

  const columns: Array<Column<Invitation>> = [
    {
      key: "code",
      header: "邀请码",
      render: (inv) => <span className="xz-code xz-adm__mono">{invitationCode(inv)}</span>,
    },
    {
      key: "roles",
      header: "角色",
      render: (inv) => (
        <span className="xz-adm__tags">
          {inv.roles.map((r) => (
            <StatusTag key={r} icon="ui-user">
              {ROLE_LABEL[r]}
            </StatusTag>
          ))}
          {inv.annotator_track && (
            <StatusTag icon="ui-target" tone="info">
              {ANNOTATOR_TRACK_LABEL[inv.annotator_track] ?? inv.annotator_track}
            </StatusTag>
          )}
        </span>
      ),
    },
    {
      key: "class",
      header: "班级",
      render: (inv) => inv.class_id ?? <span className="gz-muted">—</span>,
    },
    {
      key: "state",
      header: "状态",
      render: (inv) => {
        const meta = INVITE_STATE_META[invitationState(inv)];
        return (
          <StatusTag icon={meta.icon} tone={meta.tone}>
            {meta.label}
          </StatusTag>
        );
      },
    },
    {
      key: "uses",
      header: "已用",
      render: (inv) =>
        inv.use_count == null && inv.max_uses === undefined ? (
          <span className="gz-muted">—</span>
        ) : (
          <span>
            {inv.use_count ?? 0}/{inv.max_uses == null ? "不限" : inv.max_uses}
          </span>
        ),
    },
    { key: "expires", header: "有效期至", render: (inv) => formatDateTime(inv.expires_at) },
    {
      key: "used",
      header: "使用时间",
      hideOnMobile: true,
      render: (inv) => formatDateTime(inv.used_at),
    },
    {
      key: "actions",
      header: "操作",
      render: (inv) =>
        invitationState(inv) === "pending" || invitationState(inv) === "used" ? (
          <Button
            size="sm"
            variant="danger"
            loading={revoke.isPending}
            onClick={() => void askRevoke(inv)}
          >
            撤销
          </Button>
        ) : (
          <span className="gz-muted">—</span>
        ),
    },
  ];

  return (
    <Section title="邀请记录" subtitle="可用次数用完、过期或撤销之后失效。撤销会留下记录。">
      <QueryBoundary
        isPending={q.isPending}
        isError={q.isError}
        error={q.error}
        onRetry={() => void q.refetch()}
        skeletonRows={2}
      >
        <Table
          caption="邀请链接记录"
          columns={columns}
          rows={q.data?.invitations ?? []}
          rowKey={(inv) => invitationCode(inv)}
          empty={
            <EmptyState
              title="还没有邀请记录"
              description="点右上角「生成邀请链接」邀请专家或课程团队成员。"
            />
          }
        />
      </QueryBoundary>
    </Section>
  );
}

// --------------------------------------------------------------------------- //
// 生成邀请链接
// --------------------------------------------------------------------------- //

function RoleChoices({ value, onChange }: { value: Role[]; onChange: (next: Role[]) => void }) {
  return (
    <fieldset className="xz-form__choices">
      <legend>角色（至少一项）</legend>
      {ROLE_ORDER.map((r) => (
        <label key={r} className="xz-form__choice">
          <input
            type="checkbox"
            checked={value.includes(r)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, r] : value.filter((x) => x !== r))
            }
          />
          <span>
            <span>{ROLE_LABEL[r]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

type InviteTargetKey = (typeof INVITE_TARGETS)[number]["key"];

/** Union of the roles behind the selected 邀请对象 (教师 → course_team). */
export function rolesForTargets(keys: readonly InviteTargetKey[]): Role[] {
  const out = new Set<Role>();
  for (const k of keys) {
    const t = INVITE_TARGETS.find((x) => x.key === k);
    for (const r of t?.roles ?? []) out.add(r);
  }
  return ROLE_ORDER.filter((r) => out.has(r));
}

export function InviteSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [targets, setTargets] = useState<InviteTargetKey[]>(["annotator"]);
  const [classId, setClassId] = useState("");
  const [track, setTrack] = useState<AnnotatorTrack | "">("E1_phonetics");
  const [expiry, setExpiry] = useState("72");
  const [maxUses, setMaxUses] = useState("");
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<Invitation | null>(null);

  const classes = useQuery({
    queryKey: qk.classes,
    queryFn: ({ signal }) => classesApi.list(signal),
    enabled: open,
  });

  const selected = INVITE_TARGETS.filter((t) => targets.includes(t.key));
  const needsClass = selected.some((t) => t.needsClass);
  const needsTrack = selected.some((t) => t.needsTrack);
  const roles = rolesForTargets(targets);
  const missingClass = needsClass && classId === "";
  const canSubmit = targets.length > 0 && !missingClass;

  const create = useMutation({
    mutationFn: () =>
      invitationsApi.create({
        roles,
        ...(needsClass && classId ? { class_id: classId } : {}),
        ...(needsTrack && track ? { annotator_track: track } : {}),
        expires_hours: Number(expiry),
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(maxUses === "unlimited"
          ? { max_uses: null }
          : maxUses
            ? { max_uses: Number(maxUses) }
            : {}),
      }),
    onSuccess: (inv) => {
      setCreated(inv);
      toast.success("邀请已生成，把邀请码或链接发给本人");
      void qc.invalidateQueries({ queryKey: qk.adminInvitations });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const close = () => {
    setCreated(null);
    onClose();
  };

  if (created) {
    const code = invitationCode(created);
    return (
      <Sheet
        open={open}
        onClose={close}
        title="邀请已生成"
        footer={
          <Button variant="secondary" onClick={close}>
            我已复制
          </Button>
        }
      >
        <div className="xz-form">
          <div className="gz-notice gz-notice--success" role="status">
            <Icon id="ui-key" size={20} />
            <span>
              邀请码和链接只显示这一次，关闭后无法再查看。有效期至{" "}
              {formatDateTime(created.expires_at)}。
            </span>
          </div>

          <p className="xz-form__secret xz-adm__mono" data-testid="invite-code">
            {code}
          </p>

          <CopyField label="邀请码" value={code} hint="对方也可以在登录页直接输入这串邀请码。" />
          <CopyField
            label="邀请链接"
            value={created.url}
            hint="对方打开后自行设置用户名、密码和显示名；用过即失效。"
          />

          <KeyValue
            items={[
              {
                key: "roles",
                label: "预设角色",
                value: created.roles.map((r) => ROLE_LABEL[r]).join(" · "),
              },
              { key: "class", label: "班级", value: created.class_id ?? "—" },
              {
                key: "track",
                label: "专家方向",
                value: created.annotator_track
                  ? (ANNOTATOR_TRACK_LABEL[created.annotator_track] ?? created.annotator_track)
                  : "—",
              },
            ]}
          />
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      open={open}
      onClose={close}
      title="生成邀请链接"
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            取消
          </Button>
          <Button disabled={!canSubmit} loading={create.isPending} onClick={() => create.mutate()}>
            生成邀请
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <p className="gz-muted">
          邀请一次性有效，对方用邀请码或链接自行设定用户名与密码。管理员不设定、也看不到任何密码。
          学生不走这条路，用班级邀请码自助注册。
        </p>

        <fieldset className="xz-form__choices">
          <legend>邀请对象（可多选，例如教师同时做标注专家）</legend>
          {INVITE_TARGETS.map((t) => (
            <label key={t.key} className="xz-form__choice">
              <input
                type="checkbox"
                checked={targets.includes(t.key)}
                onChange={(e) =>
                  setTargets((prev) =>
                    e.target.checked ? [...prev, t.key] : prev.filter((x) => x !== t.key),
                  )
                }
              />
              <span>
                <span>{t.label}</span>
                <span className="gz-caption">{t.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {needsTrack && (
          <Field label="专家方向" required hint="派发式标注按 E1 + E2 配对（docs/ROADMAP.md B4）。">
            <Select
              value={track}
              onChange={(e) => setTrack(e.target.value as AnnotatorTrack | "")}
              options={[
                { value: "", label: "暂不设定" },
                ...INVITE_TRACKS.map((t) => ({ value: t.value, label: `${t.label} — ${t.hint}` })),
              ]}
            />
          </Field>
        )}

        {needsClass && (
          <Field
            label="班级"
            required
            hint="教师账号必须绑定班级：该班学生的录音会对其回避。"
            {...(missingClass ? { error: "教师邀请必须选一个班级。" } : {})}
          >
            {classes.isError ? (
              <Input
                value={classId}
                placeholder="班级 id"
                onChange={(e) => setClassId(e.target.value)}
              />
            ) : (
              <Select
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                options={[
                  { value: "", label: "请选择班级" },
                  ...(classes.data?.classes ?? []).map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            )}
          </Field>
        )}

        <Field label="备注（可选）">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>

        <Field label="可用次数" hint="不填就是一次。填了之后，用完之前都可以撤销。">
          <Select
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            options={[
              { value: "", label: "1 次（默认）" },
              { value: "20", label: "20 次" },
              { value: "unlimited", label: "不限次数" },
            ]}
          />
        </Field>

        <Field label="有效期">
          <Select
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            options={INVITE_EXPIRY_OPTIONS}
          />
        </Field>

        <p className="gz-caption">
          将授予角色：
          {roles.length === 0 ? "（尚未选择）" : roles.map((r) => ROLE_LABEL[r]).join(" · ")}
        </p>
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// 编辑角色 / 方向
// --------------------------------------------------------------------------- //

const CLASS_SCOPED: Role[] = ["student", "teacher", "annotator", "adjudicator"];

function membershipMap(user: AdminUser): Record<string, Role[]> {
  const out: Record<string, Role[]> = {};
  for (const row of user.memberships ?? []) {
    const list = out[row.class_id] ?? [];
    if (!list.includes(row.role)) list.push(row.role);
    out[row.class_id] = list;
  }
  return out;
}

function EditUserSheet({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [roles, setRoles] = useState<Role[]>(user.roles);
  const [displayName, setDisplayName] = useState(user.display_name);
  const [track, setTrack] = useState<AnnotatorTrack | "">(user.track ?? "");
  const [memberships, setMemberships] = useState<Record<string, Role[]>>(() => membershipMap(user));

  const classes = useQuery({
    queryKey: qk.classes,
    queryFn: ({ signal }) => classesApi.list(signal),
  });

  const toggleClassRole = (classId: string, role: Role, on: boolean) => {
    setMemberships((prev) => {
      const current = prev[classId] ?? [];
      const next = on ? [...current, role] : current.filter((r) => r !== role);
      return { ...prev, [classId]: next };
    });
  };

  const save = useMutation({
    mutationFn: () =>
      usersApi.update(user.id, {
        roles,
        display_name: displayName.trim(),
        ...(roles.includes("annotator") ? { track: track || null } : {}),
        ...(classes.data
          ? {
              memberships: Object.entries(memberships)
                .filter(([, picked]) => picked.length > 0)
                .map(([class_id, picked]) => ({ class_id, roles: picked })),
            }
          : {}),
      }),
    onSuccess: () => {
      toast.success(`已更新 ${user.username}`);
      void qc.invalidateQueries({ queryKey: qk.adminUsers });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title={`编辑 ${user.username}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={roles.length === 0 || displayName.trim() === ""}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            保存
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <Field label="姓名" required>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <RoleChoices value={roles} onChange={setRoles} />
        {roles.includes("annotator") && (
          <Field label="专家方向" hint="改动会写入审计记录。">
            <Select
              value={track}
              onChange={(e) => setTrack(e.target.value as AnnotatorTrack | "")}
              options={[
                { value: "", label: "暂不设定" },
                ...ANNOTATOR_TRACKS.map((t) => ({ value: t.value, label: t.label })),
              ]}
            />
          </Field>
        )}
        <p className="gz-caption">全局角色里的管理员和课程团队只有管理员能改。班级里的身份在下面勾选。</p>
        {classes.data && (
          <fieldset className="xz-form__choices">
            <legend>班级身份</legend>
            {classes.data.classes.map((c) => (
              <div key={c.id} className="xz-stack">
                <span>{c.name}</span>
                <span className="xz-adm__tags">
                  {CLASS_SCOPED.map((role) => (
                    <label key={role} className="xz-form__choice">
                      <input
                        type="checkbox"
                        checked={(memberships[c.id] ?? []).includes(role)}
                        onChange={(e) => toggleClassRole(c.id, role, e.target.checked)}
                      />
                      <span>{ROLE_LABEL[role]}</span>
                    </label>
                  ))}
                </span>
              </div>
            ))}
          </fieldset>
        )}
        <p className="gz-caption">全局角色降级会立即吊销该账号的全部会话。改班级身份不会。</p>
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// 审计抽屉
// --------------------------------------------------------------------------- //

function detailText(detail: unknown): string | null {
  if (detail === null || detail === undefined) return null;
  if (typeof detail === "string") return detail;
  try {
    return JSON.stringify(detail);
  } catch {
    return null;
  }
}

function AuditSheet({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const q = useQuery({
    queryKey: qk.adminAudit(user.id),
    queryFn: ({ signal }) => auditApi.list({ user_id: user.id, limit: 100 }, signal),
  });

  const entries: AuditEntry[] = q.data?.entries ?? [];

  return (
    <Sheet
      open
      onClose={onClose}
      title={`${user.username} 的审计记录`}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          关闭
        </Button>
      }
    >
      <KeyValue
        items={[
          { key: "name", label: "姓名", value: user.display_name },
          { key: "roles", label: "角色", value: user.roles.map((r) => ROLE_LABEL[r]).join(" · ") },
          { key: "login", label: "最近登录", value: formatDateTime(user.last_login_at) },
          { key: "sessions", label: "会话数", value: user.session_count ?? "—" },
        ]}
      />

      <p className="gz-caption" style={{ marginTop: "var(--gz-sp-4)" }}>
        审计只记录动作，不含密码，也不含任何标注内容（AUTHZ.md）。
      </p>

      {q.isPending ? (
        <LoadingBlock rows={3} label="正在加载审计记录" />
      ) : q.isError ? (
        <ErrorNotice error={q.error} onRetry={() => void q.refetch()} title="审计记录没能加载" />
      ) : entries.length === 0 ? (
        <EmptyState
          title="还没有审计记录"
          description="这个账号还没有产生可记录的动作。"
          size="sm"
        />
      ) : (
        <ul className="xz-audit">
          {entries.map((e, i) => {
            const detail = detailText(e.detail);
            return (
              <li key={e.id ?? i} className="xz-audit__row">
                <span className="xz-audit__action">{auditActionLabel(e.action)}</span>
                <span className="gz-caption">{formatDateTime(e.at ?? e.created_at)}</span>
                {e.target && <p className="xz-audit__detail">对象：{e.target}</p>}
                {detail && detail !== "null" && <p className="xz-audit__detail">{detail}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
}
